<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        md="9"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableUsers.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Role"
          label-for="filterRole"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableUsers.selected.role"
            placeholder="filter role here"
            input-id="filterRole"
            label="name"
            clearable
            append-to-body
            :options="(tableUsers.options.roles)"
            :loading="(tableUsers.fetching.roles)"
            :disabled="(tableUsers.busy || tableUsers.fetching.roles)"
            :calculate-position="calculateDropPosition"
          >
            <template #option="{ name }">
              <div
                class="text-break text-wrap"
                v-text="name"
              />
            </template>
            <template #selected-option="{ name }">
              <div
                class="w-225px w-md-100px w-lg-200px text-truncate"
                v-text="name"
              />
            </template>
            <template #no-options="">
              no available role
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
      >
        <BButton
          size="sm"
          type="button"
          variant="primary"
          @click.prevent="createUser"
        >
          Create User
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableUsers"
      :provider="tableProvider"
      :fields="tableUsers.fields"
      :filter="tableUsers.filter"
      :filter-on="tableUsers.filterOn"
      :sort-by="tableUsers.sortBy"
      :sort-desc="tableUsers.sortDesc"
      :sort-direction="tableUsers.sortDirection"
      :total-rows="tableUsers.totalRows"
      :current-page="tableUsers.currentPage"
      :per-page="tableUsers.perPage"
      :stacked="tableUsers.stacked"
      :busy="tableUsers.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableUsers.busy"
            @click.prevent="editUser(item)"
          >
            Edit
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalUser"
      size="xl"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
      @shown="() => attachArrowScroller('.nav-users-tabs')"
      @hidden="() => detachArrowScroller('.nav-users-tabs')"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-primary"
        nav-class="scrollable-x-tabs no-scrollbar nav-users-tabs gap-x-1"
        content-class="scrollable"
        pills
      >
        <template #tabs-start>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-left bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <template #tabs-end>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="right: 20px; padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-right bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-file-lines', {
                  'text-danger fat fa-shake': vTabUserDetails()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabUserDetails()
                }]"
              >
                Details
              </b>
            </div>
          </template>

          <ValidationObserver
            ref="formUser"
          >
            <form
              role="form"
              novalidate
              @submit.prevent
            >
              <BRow>

                <BCol
                  cols="12"
                  md="4"
                >
                  <WVInput
                    v-model="formUser.first_name"
                    :disabled="state.busy"
                    :validation-attr="{
                      name: 'first name',
                      vid: 'first_name',
                      rules: 'required|max:100'
                    }"
                    :form-group-attr="{
                      label: 'First Name',
                      labelClass: 'font-weight-bolder',
                      class: 'mb-2'
                    }"
                    placeholder="enter first name here"
                    autocomplete="off"
                    maxlength="100"
                    type="text"
                  />
                </BCol>

                <BCol
                  cols="12"
                  md="4"
                >
                  <WVInput
                    v-model="formUser.last_name"
                    :disabled="state.busy"
                    :validation-attr="{
                      name: 'last name',
                      vid: 'last_name',
                      rules: 'required|max:100'
                    }"
                    :form-group-attr="{
                      label: 'Last Name',
                      labelClass: 'font-weight-bolder',
                      class: 'mb-2'
                    }"
                    placeholder="enter last name here"
                    autocomplete="off"
                    maxlength="100"
                    type="text"
                  />
                </BCol>

                <BCol cols="12" />

                <BCol
                  cols="12"
                  md="4"
                >
                  <WVInput
                    v-model="formUser.mobile_number"
                    :disabled="state.busy"
                    :validation-attr="{
                      name: 'mobile number',
                      vid: 'mobile_number',
                      rules: 'max:11|min:11'
                    }"
                    :form-group-attr="{
                      label: 'Mobile Number',
                      labelClass: 'font-weight-bolder',
                      class: 'mb-2'
                    }"
                    placeholder="enter mobile number here"
                    autocomplete="off"
                    maxlength="11"
                    type="text"
                  />
                </BCol>

                <BCol
                  cols="12"
                  md="8"
                >
                  <WVInput
                    v-model="formUser.email"
                    :disabled="state.busy"
                    :validation-attr="{
                      name: 'email',
                      vid: 'email',
                      rules: 'email'
                    }"
                    :form-group-attr="{
                      label: 'Email',
                      labelClass: 'font-weight-bolder',
                      class: 'mb-2'
                    }"
                    placeholder="enter email here"
                    autocomplete="off"
                    type="email"
                  />
                </BCol>

                <BCol
                  cols="12"
                  md="4"
                />

                <BCol
                  cols="12"
                  md="8"
                >
                  <WVInput
                    v-model="formUser.notification_email"
                    :disabled="state.busy"
                    :validation-attr="{
                      name: 'notification email',
                      vid: 'notification_email',
                      rules: 'email'
                    }"
                    :form-group-attr="{
                      label: 'Notification Email',
                      labelClass: 'font-weight-bolder',
                      class: 'mb-2'
                    }"
                    placeholder="enter notification email here"
                    autocomplete="off"
                    type="email"
                  />
                </BCol>

                <BCol
                  cols="12"
                  md="4"
                >
                  <WVInput
                    v-model="formUser.username"
                    placeholder="enter username here"
                    :disabled="state.busy"
                    :validation-attr="{
                      name: 'username',
                      vid: 'username',
                      rules: 'max:50'
                    }"
                    :form-group-attr="{
                      label: 'Username',
                      labelClass: 'font-weight-bolder',
                      class: 'mb-2'
                    }"
                    autocomplete="off"
                    maxlength="50"
                    type="text"
                  />
                </BCol>

                <BCol
                  cols="12"
                  md="4"
                >
                  <WVInput
                    v-model="formUser.password"
                    :disabled="state.busy"
                    :validation-attr="{
                      name: 'password',
                      vid: 'password',
                      rules: vPassword
                    }"
                    :form-group-attr="{
                      label: 'Password',
                      labelClass: 'font-weight-bolder',
                      class: 'mb-2'
                    }"
                    placeholder="enter password here"
                    autocomplete="off"
                    type="password"
                  />
                </BCol>

                <BCol
                  cols="12"
                  md="4"
                >
                  <WVInput
                    v-model="formUser.password_confirmation"
                    :disabled="state.busy"
                    :validation-attr="{
                      name: 'password confirmation',
                      vid: 'password_confirmation',
                      rules: vPasswordConfirmation
                    }"
                    :form-group-attr="{
                      label: 'Password Confirmation',
                      labelClass: 'font-weight-bolder',
                      class: 'mb-2'
                    }"
                    placeholder="enter password confirmation here"
                    autocomplete="off"
                    type="password"
                  />
                </BCol>

                <BCol
                  cols="12"
                  md="4"
                >
                  <WVVSelect
                    v-model="selected.role"
                    :validation-attr="{
                      name: 'role',
                      vid: 'role_id',
                      rules: 'required'
                    }"
                    :form-group-attr="{
                      label: 'Role',
                      labelClass: 'font-weight-bolder',
                      class: 'mb-2'
                    }"
                    :options="options.roles"
                    :disabled="(state.busy || fetching.roles || isFirstUser)"
                    :loading="fetching.roles"
                    label="name"
                    placeholder="search role here"
                    @search="searchRoles"
                  >
                    <template #option="{ name }">
                      <div
                        class="text-break text-wrap"
                        v-text="name"
                      />
                    </template>
                    <template #no-options="">
                      no available roles
                    </template>
                  </WVVSelect>
                </BCol>

                <BCol
                  cols="12"
                  md="4"
                >
                  <WVVSelect
                    v-model="selected.team"
                    :validation-attr="{
                      name: 'team',
                      vid: 'team_id',
                      rules: 'required'
                    }"
                    :form-group-attr="{
                      label: 'Team',
                      labelClass: 'font-weight-bolder',
                      class: 'mb-2'
                    }"
                    :options="options.teams"
                    :disabled="(state.busy || fetching.teams || formUser.clusters.length > 0)"
                    :loading="fetching.teams"
                    label="team_name"
                    placeholder="search team here"
                    @search="searchTeams"
                  >
                    <template #option="{ team_name }">
                      <div
                        class="text-break text-wrap"
                        v-text="team_name"
                      />
                    </template>
                    <template #list-footer>
                      <li class="text-right p-1">
                        <router-link
                          class="text-decoration-none"
                          :to="{ name: 'admin.locations.teams' }"
                        >
                          Create Team
                        </router-link>
                      </li>
                    </template>
                    <template #no-options="">
                      no available teams
                    </template>
                  </WVVSelect>
                </BCol>

                <BCol
                  cols="12"
                  md="4"
                >
                  <WVVSelect
                    v-model="selected.area"
                    :validation-attr="{
                      name: 'area',
                      vid: 'area_id',
                      rules: 'required'
                    }"
                    :form-group-attr="{
                      label: 'Area',
                      labelClass: 'font-weight-bolder',
                      class: 'mb-2'
                    }"
                    :options="options.areas"
                    :disabled="(state.busy || fetching.areas || formUser.clusters.length > 0)"
                    :loading="fetching.areas"
                    label="area_name"
                    placeholder="search area here"
                    @search="searchAreas"
                  >
                    <template #option="{ area_name }">
                      <div
                        class="text-break text-wrap"
                        v-text="area_name"
                      />
                    </template>
                    <template #list-footer>
                      <li class="text-right p-1">
                        <router-link
                          class="text-decoration-none"
                          :to="{ name: 'admin.locations.areas' }"
                        >
                          Create Area
                        </router-link>
                      </li>
                    </template>
                    <template #no-options="">
                      no available areas
                    </template>
                  </WVVSelect>
                </BCol>

                <BCol
                  v-if="state.editing"
                  cols="12"
                >
                  <BFormGroup
                    class="mb-2"
                  >
                    <BFormCheckbox
                      id="is_active"
                      v-model="formUser.active"
                      switch
                      value="1"
                      name="is_active"
                      unchecked-value="0"
                      :disabled="state.busy|| isFirstUser"
                    >
                      Is Active?
                    </BFormCheckbox>
                  </BFormGroup>
                </BCol>

              </BRow>
            </form>
          </ValidationObserver>
        </BTab>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-map-location-dot" />
              <b class="ml-1">Clusters</b>
            </div>
          </template>

          <Clusters
            ref="tableUserClusters"
            :area="selected.area"
            :clusters="formUser.clusters"
            @updated="updateUserClusters"
          />
        </BTab>
      </BTabs>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateUser"
        >
          {{ state.editing ? 'Update' : 'Create' }} User
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import QUERY from 'lodash'

import Clusters from '@/views/admin/accounts/users/Clusters.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MRoles from '@/mixins/accounts/roles'
import MAreas from '@/mixins/locations/areas'
import MTeams from '@/mixins/locations/teams'

import { SAdminUser } from '@/services/admin'

export default {
  name: 'VAdminAccountsUsers',

  components: {
    Clusters
  },

  mixins: [
    MRoles,
    MTeams,
    MAreas,
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Users'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false,
        visible_password: false
      },
      formUser: {
        id: '',
        role_id: '',
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        notification_email: '',
        mobile_number: '',
        password: '',
        password_confirmation: '',
        team_id: '',
        area_id: '',
        clusters: [],
        active: 1
      },
      tableUsers: {
        busy: false,
        fetching: {
          areas: false,
          roles: false
        },
        options: {
          areas: [],
          roles: []
        },
        selected: {
          area: '',
          role: ''
        },
        filter: {
          search: '',
          area: '',
          role: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 2, key: 'username', class: 'min-w-150px' },
          { mobile: 3, key: 'first_name', label: 'Name', class: 'min-w-150px', formatter: this.getFullName },
          { mobile: 4, key: 'mobile_number', class: 'text-md-right min-w-150px' },
          { mobile: 5, key: 'email', class: 'min-w-md-175px' },
          { mobile: 6, key: 'notification_email', class: 'min-w-md-175px' },
          { mobile: 7, key: 'area.area_name', label: 'Area' },
          { mobile: 8, key: 'clusters.length', label: 'Assigned Clusters' },
          { mobile: 9, key: 'role.role_name', label: 'Role' },
          { mobile: 10, key: 'active', class: 'text-center w-md-75px', formatter: this.yesOrNo },
          { mobile: 11, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 12, key: 'last_login', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: Object.assign({
    MUserTitle () {
      return this.state.editing ? 'Edit User' : 'Add User'
    },

    vPassword () {
      return `${this.state.editing ? '' : 'required|'}min:8`
    },

    vPasswordConfirmation () {
      return `${this.state.editing ? '' : 'required|'}confirmed:password`
    },

    passwordType () {
      return this.state.visible_password ? 'text' : 'password'
    },

    isFirstUser () {
      return Number(this.formUser.id) === 1
    }
  }, mapGetters({
    currentUser: 'auth/user'
  })),

  watch: {
    'selected.role' (role) {
      this.formUser.role_id = role?.id || ''
    },

    'selected.team' (team) {
      this.formUser.team_id = team?.id || ''
    },

    'selected.area' (area) {
      this.formUser.area_id = area?.id || ''
    },

    'tableUsers.selected.role' (role) {
      this.tableUsers.filter.role = role?.id || ''
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getRoles(this.tableUsers)
    }
  },

  methods: {

    vTabUserDetails () {
      if (this.$refs.formUser) {
        const formUserErrors = this.$refs.formUser.errors
        return Object.keys(formUserErrors).some(
          errorKey => formUserErrors[errorKey]?.length > 0
        )
      }

      return false
    },

    togglePasswordType () {
      this.state.visible_password = !this.state.visible_password
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableUsers.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        role_id: filter.role
      }

      return await SAdminUser.get(payload).then(
        ({ data }) => {
          this.tableUsers.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableUsers.totalRows = 0
        return []
      }).finally(() => {
        this.tableUsers.busy = false
      })
    },

    updateUserClusters (clusters) {
      this.formUser.clusters = clusters
    },

    createUser () {
      this.state.editing = false

      this.formUser = Object.assign(this.formUser, {
        id: '',
        role_id: '',
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        notification_email: '',
        mobile_number: '',
        password: '',
        password_confirmation: '',
        team_id: '',
        area_id: '',
        cluster_id: '',
        clusters: [],
        active: 1
      })

      this.getRoles(this)

      this.getTeams(this)

      this.$refs.modalUser.show()
    },

    editUser (user) {
      this.state.editing = true

      this.editable.area = user.area_id

      this.formUser = Object.assign(this.formUser, {
        id: user.id,
        role_id: user.role_id,
        username: user.username,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        notification_email: user.notification_email,
        mobile_number: user.mobile_number,
        password: '',
        password_confirmation: '',
        team_id: '',
        area_id: '',
        clusters: user?.clusters || [],
        active: user.active
      })

      this.getRoles(this).finally(() => {
        this.selected.role = QUERY.find(this.options.roles, {
          id: Number(user?.role_id)
        }) || ''
      })

      this.getTeams(this).finally(() => {
        this.selected.team = QUERY.find(this.options.teams, {
          id: Number(user?.team_id)
        }) || ''
      })

      this.$refs.modalUser.show()
    },

    async validateUser () {
      await this.$refs.formUser.validate().then(
        async validUser => {
          if (!validUser) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this User?</h6>' : '<h6>Create this User?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putUser() : this.postUser()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    this.$refs.formUser.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postUser () {
      return await SAdminUser.post(this.formUser).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalUser.hide()
            this.$refs.tableUsers.refetch()
          })
        }
      )
    },

    async putUser () {
      return await SAdminUser.put(this.formUser).then(
        ({ data: { message, user } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalUser.hide()
            this.$refs.tableUsers.updateRow({
              id: user.id,
              username: user.username,
              first_name: user.first_name,
              last_name: user.last_name,
              email: user.email,
              notification_email: user.notification_email,
              mobile_number: user.mobile_number,
              role: user.role,
              role_id: user.role_id,
              team: user.team,
              team_id: user.team_id,
              area: user.area,
              area_id: user.area_id,
              clusters: user.clusters,
              active: user.active,
              updated_at: user.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
